<template>
    <BaseForm v-if="series">
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <TextAreaField
                :value="values.notes"
                :label="$t('notes')"
                :error-messages="errors.notes"
                :max-length="projectSeriesIdentifiersSchema.shape.notes.maxLength ?? undefined"
                @input="setFieldValue('notes', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notes', true)"
            />
            <TextAreaField
                :value="values.description"
                :label="$t('description')"
                :max-length="projectSeriesIdentifiersSchema.shape.description.maxLength ?? undefined"
                :error-messages="errors.description"
                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('description', true)"
            />
            <MultiLink
                :label="$t('project-websites')"
                :links="values.websites"
                :error-messages="errors.websites"
                @input="setFieldValue('websites', $event)"
                @delete="setFieldValue('websites', $event)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import { useForm } from '@/forms/composables/useForm';
import { useProjectSeriesInjection } from '@/project/composables/useProjectSeriesInjection';
import { getProjectSeriesIdentifiersSchema } from '@/project/schemas';
import { computed } from 'vue';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);

const projectSeriesIdentifiersSchema = computed(() => getProjectSeriesIdentifiersSchema(seriesId.value));

const { series, updateProjectSeries, createProjectSeriesMultiLink } = useProjectSeriesInjection();

const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createProjectSeriesMultiLink);

const websitesId = computed(() => series.value?.websites?.id);
const websitesLinks = computed(() => series.value?.websites?.links.nodes ?? []);

const { values, errors, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    projectSeriesIdentifiersSchema.value,
    computed(() => ({
        nameShort: series.value?.nameShort ?? '',
        nameLong: series.value?.nameLong ?? '',
        notes: series.value?.notes ?? '',
        description: series.value?.description ?? '',
        websites: getFormFieldLinks(websitesLinks),
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateProjectSeries({ id: seriesId.value, ...values.value });
        await saveMultiLinkFields([{ id: websitesId.value, fieldName: 'websites', links: values.value.websites }]);
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
